import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useSharedLogic } from "./use-shared-logic";

export type PermissionState = "granted" | "denied" | "prompt";

export type CameraPermission = {
	requestPermission: () => Promise<boolean>;
	checkPermission: () => Promise<PermissionState>;
};

function useWebCameraPermission(): CameraPermission {
	const checkPermission = useCallback(async (): Promise<PermissionState> => {
		if (navigator.permissions) {
			try {
				const status = await navigator.permissions.query({
					name: "camera" as PermissionName,
				});
				return status.state as PermissionState;
			} catch {
				return "prompt";
			}
		}
		return "prompt";
	}, []);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Error handling requires multiple branches
	const requestPermission = useCallback(async (): Promise<boolean> => {
		const state = await checkPermission();
		if (state === "denied") {
			toast.error(
				"Camera access blocked. Please reset your browser's site settings to allow camera access.",
				{ duration: 6000 }
			);
			return false;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: true,
			});
			for (const track of stream.getTracks()) {
				track.stop();
			}
			return true;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			if (
				errorMessage.includes("NotReadableError") ||
				errorMessage.includes("Could not start video source")
			) {
				toast.error(
					"Camera is in use by another application or permission was denied"
				);
			} else if (
				errorMessage.includes("NotAllowedError") ||
				errorMessage.includes("Permission denied")
			) {
				toast.error(
					"Camera access denied. Please check your browser settings to allow camera access",
					{ duration: 6000 }
				);
			} else if (errorMessage.includes("NotFoundError")) {
				toast.error("No camera found. Please connect a camera and try again");
			}
			return false;
		}
	}, [checkPermission]);

	return useMemo(
		() => ({ requestPermission, checkPermission }),
		[requestPermission, checkPermission]
	);
}

function useElectronCameraPermission(): CameraPermission {
	const checkPermission = useCallback(async (): Promise<PermissionState> => {
		if (!window.api?.media) return "prompt";
		const status = await window.api.media.checkPermission("camera");
		if (status === "granted") return "granted";
		if (status === "denied" || status === "restricted") return "denied";
		return "prompt";
	}, []);

	const requestPermission = useCallback(async (): Promise<boolean> => {
		if (!window.api?.media) return false;
		const state = await checkPermission();
		if (state === "denied") {
			toast.error(
				"Camera access blocked. Please enable camera access in System Preferences > Security & Privacy > Privacy > Camera.",
				{ duration: 6000 }
			);
			return false;
		}

		const granted = await window.api.media.requestCameraPermission();
		if (!granted) {
			toast.error(
				"Camera access denied. Please enable camera access in System Preferences.",
				{ duration: 6000 }
			);
		}
		return granted;
	}, [checkPermission]);

	return useMemo(
		() => ({ requestPermission, checkPermission }),
		[requestPermission, checkPermission]
	);
}

export function useCameraPermission(): CameraPermission {
	return useSharedLogic({
		web: useWebCameraPermission,
		electron: useElectronCameraPermission,
	}) as CameraPermission;
}
